import type { ITableLikeTreeItemNotification } from 'components/TableLikeTree/context';
import { RequestStatus } from 'features/request/requestSlice';
import type { ConstructionTypeData } from 'gql/graphql';
import { ContractObjectStatusEnum } from 'gql/graphql';
import type {
    IConstructionTypeTabItem,
    IUseGetContractStructureDataSourceProps,
    IUseGetContractStructureDataSourceResult,
} from 'pages/Project/Common/Contract/ContractStructure/types';
import { Badge } from 'uikit';
import type { SelectedFilters, TableColumn } from 'uikit';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useContractData } from '../../hooks/useContractData';

import type { TContractObjectData, TFilters } from './helpers';
import { getFilteredItems } from './helpers';
import { useUerContractObjects } from './useUerContractObjects';

const additionalColumns: TableColumn<any>[] = [
    {
        title: 'Гарантированный объем',
        align: 'center',
        accessor: 'hasGuaranteedVolume',
        width: 200,
    },
    {
        title: 'Код',
        accessor: 'codeWBS',
        width: 300,
    },
];

export const useGetUerContractStructureDataSource = (
    props: IUseGetContractStructureDataSourceProps,
): IUseGetContractStructureDataSourceResult<TContractObjectData> => {
    const { contractId } = props;

    const [contractData, contractStatus] = useContractData(contractId as number);

    const [activeFilterValue, setActiveFilterValue] = useState<TFilters>();

    const [activeConstructionTypeTabItem, setActiveConstructionTypeTabItem] = useState<
        IConstructionTypeTabItem | undefined
    >();

    const constructionTypesTabsItems = useMemo(
        () =>
            contractData?.constructionTypes
                ?.map((type: ConstructionTypeData) => ({ id: type.id, label: type.name }))
                .sort((a: IConstructionTypeTabItem, b: IConstructionTypeTabItem) => a.label.localeCompare(b.label)),
        [contractData?.constructionTypes],
    );

    const { uerContractObjectsMakeRequest, uerContractObjectsData, uerContractObjectsStatus } =
        useUerContractObjects(contractId);

    const isLoading = useMemo(
        () => contractStatus === RequestStatus.FETCHING || uerContractObjectsStatus === RequestStatus.FETCHING,
        [contractStatus, uerContractObjectsStatus],
    );

    const items = useMemo(() => {
        const mappedItems = uerContractObjectsData?.uerContractObjects?.map((object) => ({
            ...object,
            hasGuaranteedVolume: object.hasGuaranteedVolume ? 'Да' : 'Нет',
        }));

        return getFilteredItems(mappedItems, activeFilterValue);
    }, [activeFilterValue, uerContractObjectsData?.uerContractObjects]);

    const informers: ITableLikeTreeItemNotification[] = useMemo(
        () =>
            items
                ?.filter((o) => o.status === ContractObjectStatusEnum.NewRecord)
                .map((o) => ({
                    items: [
                        {
                            icon: <Badge size="s" status="success" label="Д" />,
                        },
                    ],
                    treeItemId: o.id,
                })),
        [items],
    );

    const getTagLabel = useCallback((id: string, name: string, filterValue: any) => {
        const val = id === 'hasGuaranteedVolume' ? filterValue?.value : filterValue;

        return `${name}: '${val || ''}'`;
    }, []);

    const onFiltersUpdated = useCallback((currentFilters: SelectedFilters) => {
        setActiveFilterValue({
            codeWBS: currentFilters?.codeWBS?.value,
            hasGuaranteedVolume: currentFilters?.hasGuaranteedVolume?.value?.value,
        });
    }, []);

    useEffect(() => {
        if (constructionTypesTabsItems?.length) {
            setActiveConstructionTypeTabItem(constructionTypesTabsItems[0]);
        }
    }, [constructionTypesTabsItems]);

    useEffect(() => {
        if (activeConstructionTypeTabItem) {
            uerContractObjectsMakeRequest(activeConstructionTypeTabItem.id);
        }
    }, [activeConstructionTypeTabItem, uerContractObjectsMakeRequest]);

    return {
        items,
        informers,
        getTagLabel,
        onFiltersUpdated,
        isLoading,
        constructionTypesTabsItems,
        requestData: uerContractObjectsMakeRequest,
        activeConstructionTypeTabItem,
        setActiveConstructionTypeTabItem,
        additionalColumns,
    };
};
